/** @jsxImportSource preact */
import * as m from "../paraglide/messages.js";
import { SupportFeedbackProvider } from "./contact/support-feedback";
import { SupportRequestPanelProvider } from "./contact/support-request-panel";
import type { ComponentChildren } from "preact";
import { useState } from "preact/hooks";

import type { ResolvedWidgetConfig } from "../config";
import type {
  Conversation,
  ConversationState,
} from "../conversation/conversation";
import type { WidgetChatMessage } from "./message";
import {
  WidgetPanel,
  type ReplyNotice,
  type WidgetCapsule,
} from "./widget-panel";
import type { PanelSize } from "./panel-resize";
import type { ProactivePrompt } from "../proactive/proactive";
import type { CharacterReaction } from "./use-character-reaction";
import type { LauncherDrag } from "./use-launcher-drag";
import type { WidgetView } from "./view-state";
import type { VoiceViewState } from "../voice/use-voice";

export interface ConversationPanelProps {
  config: ResolvedWidgetConfig;
  conversation: Conversation;
  state: ConversationState;
  reaction?: CharacterReaction;
  /** 嵌在宿主面板里（后台 Playground）：占满盒子、常开，没有 launcher 也没有关闭。 */
  embedded?: boolean;
  height?: string;
  onResize?: (size: PanelSize | undefined) => void;
  launcherDrag?: LauncherDrag;
  panelOffset?: number;
  /** 店面移动端内容占满抽屉。 */
  drawerHandle?: ComponentChildren;
  fullscreen?: boolean;
  onToggleFullscreen?: () => void;
  mobile?: boolean;
  width?: string;
  proactive?: ProactivePrompt;
  /** 分镜显式传入；真实会话暂不推断等待补充。 */
  waitingLabel?: string;
  entrance?: "open" | "restore";
  leaving?: boolean;
  view?: WidgetView;
  voice?: VoiceViewState;
}

/**
 * 会话状态到面板 props 的唯一一处映射。店面挂件和后台 Playground 共用它，
 * 谁都不许再写第二套聊天 UI。
 */
export function ConversationPanel({
  config,
  conversation,
  state,
  reaction,
  embedded = false,
  height,
  onResize,
  launcherDrag,
  panelOffset,
  mobile = false,
  drawerHandle,
  fullscreen,
  onToggleFullscreen,
  width,
  proactive,
  waitingLabel,
  entrance,
  leaving,
  view,
  voice,
}: ConversationPanelProps) {
  const [cardAction, setCardAction] = useState(0);
  const followCardAction = () => {
    setCardAction((current) => current + 1);
  };
  const appearance = config.appearance;
  const messages: WidgetChatMessage[] =
    state.busy || state.eventWaiting
      ? [
          ...state.messages,
          {
            id: "pending",
            role: "ai",
            typing: true,
            text: state.eventWaiting
              ? m.widget_order_event_waiting()
              : state.progress,
          },
        ]
      : state.messages;
  // 气泡上的重试指名是哪条消息：send() 拿它的 id 复用那条，不另起气泡。
  const onSend = (text: string) => {
    setCardAction(0);
    void conversation.send(text);
  };
  const unreadReply = state.messages.find(
    (message) => message.id === state.unreadReplyId,
  );
  const notice: ReplyNotice | undefined = state.busy
    ? { status: "generating", progress: state.progress }
    : state.unreadError
      ? { status: "failed", dismiss: conversation.markRead }
      : unreadReply
        ? {
            status: "replied",
            text: unreadReply.text,
            dismiss: conversation.markRead,
          }
        : undefined;
  const capsule: WidgetCapsule =
    embedded || state.open
      ? { size: "large" }
      : notice
        ? { size: "medium", notice }
        : proactive
          ? { size: "medium", prompt: proactive }
          : { size: "small" };

  return (
    <SupportFeedbackProvider
      scope={`${state.conversationId ?? ""}:${state.auth.store_login}`}
    >
      <SupportRequestPanelProvider conversation={conversation} state={state}>
        <WidgetPanel
          cardAction={cardAction}
          storefront={
            config.playgroundSession === undefined &&
            config.storefrontRootUrl !== undefined
              ? {
                  rootUrl: config.storefrontRootUrl,
                  shopDomain: config.shopDomain,
                  locale: config.locale,
                }
              : undefined
          }
          onCartReceipt={conversation.noteCart}
          drawerHandle={drawerHandle}
          fullscreen={fullscreen}
          onToggleFullscreen={onToggleFullscreen}
          accent={appearance.accent}
          avatar={appearance.avatar}
          blur={appearance.blur}
          busy={state.busy}
          character={appearance.character}
          capsule={capsule}
          entrance={entrance}
          leaving={leaving}
          view={view}
          direction={appearance.direction}
          draft={state.draft}
          empty={messages.length === 0}
          emptyTitle={appearance.welcomeMessage}
          error={state.error}
          fill={embedded || (mobile && state.open)}
          font={appearance.font}
          fontSize={appearance.fontSize}
          height={height}
          onResize={onResize}
          launcherLabel={appearance.launcherLabel}
          launcherDrag={launcherDrag}
          messages={messages.map((message) => ({
            ...message,
            maxTaskVersion: state.maxTaskVersion,
            disabledBlocks: state.disabledBlocks,
          }))}
          voiceEnabled={voice?.enabled}
          voiceStatus={voice?.status}
          voiceError={voice?.error}
          onVoiceToggle={voice?.onToggle}
          verificationBusy={state.authBusy}
          orderBusy={state.orderBusy || state.eventWaiting || state.busy}
          orderFailures={state.orderFailures}
          onOrderAction={
            !embedded || config.playgroundSession !== undefined
              ? (messageId, stateIndex, action, orderRef) => {
                  followCardAction();
                  void conversation.orderAction(
                    messageId,
                    stateIndex,
                    action,
                    orderRef,
                  );
                }
              : undefined
          }
          onVerifyTask={
            config.playgroundSession !== undefined ||
            (!embedded && config.customerAccountsEnabled !== false)
              ? (messageId, email) => {
                  followCardAction();
                  void conversation.verifyTask(messageId, email);
                }
              : undefined
          }
          playgroundVerification={config.playgroundSession !== undefined}
          verificationAuth={state.auth}
          onVerifyEmail={
            embedded && !config.playgroundSession
              ? undefined
              : (messageId, input) => {
                  followCardAction();
                  return conversation.verifyTaskEmail(messageId, input);
                }
          }
          onClose={
            embedded
              ? undefined
              : () => {
                  conversation.setOpen(false);
                }
          }
          onDraftChange={conversation.setDraft}
          onOpen={() => {
            conversation.setOpen(true);
          }}
          onQuickReply={onSend}
          merchantFeedback={config.merchantFeedback}
          onRetry={(text, msgId) => {
            void conversation.send(text, msgId);
          }}
          onSend={onSend}
          panelOffset={panelOffset}
          placeholder={appearance.placeholder}
          position={appearance.position}
          proactivePromptStyle={appearance.proactivePromptStyle}
          quickReplies={state.quickReplies}
          reaction={reaction}
          waitingLabel={waitingLabel}
          radius={appearance.radius}
          replyCopyButton={appearance.replyCopyButton}
          subtitle={appearance.subtitle}
          suggestions={appearance.suggestions}
          surface={appearance.surface}
          surfaceOpacity={appearance.surfaceOpacity}
          title={appearance.title}
          width={width}
        />
      </SupportRequestPanelProvider>
    </SupportFeedbackProvider>
  );
}
